// TODO: fix button in slider
import "./index.scss";
import {
  Autoplay,
  EffectFade,
  Manipulation,
  Navigation,
  Pagination,
} from "swiper/modules";
import {
  GeneralMediumChevronLeft,
  GeneralMediumChevronRight,
} from "@wise-emotions/react-icons";
import { SliderProps } from "./types";
import { SwiperOptions } from "swiper/types";
import IconWrap from "components/Icon/IconWrap";
import React, {
  CSSProperties,
  Children,
  useEffect,
  useMemo,
  useRef,
} from "react";
import Swiper from "swiper";
import classNames from "classnames";

const Slider = ({
  children,
  id,
  on,
  autoplay,
  centeredSlides = false,
  effect = "fade",
  loop = true,
  pagination = true,
  slidesPerView = 1,
  spaceBetween = 0,
  hasList = false,
  ...rest
}: SliderProps) => {
  const swiper = useRef<Swiper>();

  const swiperChildren = Children.toArray(children).length;
  const swiperContanierId = `swiper-container--${id}`;
  const swiperNavigationNextId = `swiper-button--next--${id}`;
  const swiperNavigationPrevId = `swiper-button--prev--${id}`;
  const swiperPaginationId = `swiper-pagination--${id}`;
  const contanierClassNames = classNames(
    "swiper-container",
    swiperContanierId,
    { "swiper--hasList": hasList },
  );
  const slideClassNames = classNames("swiper-slide", {
    "swiper-slide-auto": slidesPerView === "auto",
  });

  const nextButtonClassNames = classNames(
    "swiper-button",
    "swiper-button-next",
    swiperNavigationNextId,
  );
  const prevButtonClassNames = classNames(
    "swiper-button",
    "swiper-button-prev",
    swiperNavigationPrevId,
  );
  const paginationClassNames = classNames(
    "swiper-pagination",
    swiperPaginationId,
    { "swiper-static": !autoplay },
  );
  const autoplayDelayStyle = {
    "--autoplay-delay":
      typeof autoplay === "object" && autoplay.delay
        ? `${(autoplay.delay + 300).toString()}ms`
        : null,
  } as CSSProperties;
  const generatedPagination = useMemo(() => {
    if (!pagination) return {};
    if (pagination === true) {
      return {
        el: `.${swiperPaginationId}`,
        clickable: true,
        renderBullet(_, className) {
          if (!autoplay) {
            return `<span class="${className}"></span>`;
          }
          return `<span class="${className}"><div class="swiper-pagination-bullet-progress"></div></span>`;
        },
      };
    }
    return pagination;
  }, [pagination, swiperPaginationId, autoplay]);

  const setupSwiper = () => {
    const swiperSettings: SwiperOptions = {
      autoplay: swiperChildren > 1 ? autoplay : false,
      centeredSlides,
      effect,
      loop,
      on,
      pagination: generatedPagination,
      slidesPerView,
      spaceBetween,
      modules: [Navigation, Pagination, Autoplay, Manipulation, EffectFade],
      simulateTouch: swiperChildren > 1,
      ...rest,
    };
    swiper.current = new Swiper(`.${swiperContanierId}`, swiperSettings);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(setupSwiper, [
    autoplay,
    centeredSlides,
    effect,
    generatedPagination,
    loop,
    on,
    slidesPerView,
    spaceBetween,
    swiperContanierId,
  ]);

  const clickNavigation = (nav: "prev" | "next") => {
    if (!swiper.current) return;
    if (nav === "prev") {
      swiper.current.slidePrev();
    } else {
      swiper.current.slideNext();
    }
  };

  const clickNavigationPrev = (e: React.MouseEvent) => {
    e.preventDefault();
    clickNavigation("prev");
  };
  const clickNavigationNext = (e: React.MouseEvent) => {
    e.preventDefault();
    clickNavigation("next");
  };

  return (
    <div className="slider-wrapper">
      <div className={contanierClassNames}>
        <div className="swiper-wrapper">
          {React.Children.map(children, (child) => {
            return (
              <div data-testid="child" className={slideClassNames}>
                {child}
              </div>
            );
          })}
        </div>
        {swiperChildren > 1 && (
          <>
            <div
              data-testid="pagination"
              style={autoplayDelayStyle}
              className={paginationClassNames}
            />
            <button
              // TODO: add correct aria label ( not hardcoded value )
              aria-label="Indietro"
              data-testid="prevButton"
              type="button"
              className={prevButtonClassNames}
              onClick={clickNavigationPrev}
            >
              <IconWrap size="32px">
                <GeneralMediumChevronLeft />
              </IconWrap>
            </button>
            <button
              // TODO: add correct aria label ( not hardcoded value )
              aria-label="Avanti"
              data-testid="nextButton"
              type="button"
              className={nextButtonClassNames}
              onClick={clickNavigationNext}
            >
              <IconWrap size="32px">
                <GeneralMediumChevronRight />
              </IconWrap>
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Slider;
